import React, { useState, useEffect } from 'react';
import useExpertStore from '../store/useExpertStore';
import { VIBES, VERBOSITIES, PROACTIVITIES } from '../lib/expertOptions';

const XIcon = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>;
const AlertIcon = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>;

const SectionLabel = ({ label }) => (
    <h4 className="mt-6 mb-3 text-[11px] font-bold text-[var(--text-muted)] uppercase tracking-widest">{label}</h4>
);

const ToggleGroup = ({ value, onChange, options }) => (
    <div className="flex flex-col sm:flex-row bg-[var(--bg-tertiary)] border border-[var(--border-subtle)]/50 rounded-xl overflow-hidden shadow-inner p-1 gap-1">
        {options.map((opt) => {
            const isSelected = value === opt.value;
            return (
                <button
                    key={opt.value}
                    type="button"
                    onClick={() => onChange(opt.value)}
                    className={`flex-1 flex flex-col items-center justify-center p-3 text-center rounded-lg transition-all ${isSelected
                        ? 'bg-[var(--accent-primary)] text-[var(--bg-main)] shadow-md'
                        : 'bg-transparent text-[var(--text-muted)] hover:text-[var(--text-main)] hover:bg-[var(--bg-secondary)]'
                        }`}
                >
                    <span className="text-sm font-bold mb-1">{opt.label}</span>
                    <span className={`text-[10px] leading-tight max-w-[140px] ${isSelected ? 'text-white/80' : 'text-[var(--text-muted)]'}`}>
                        {opt.description.split('.')[0]}
                    </span>
                </button>
            )
        })}
    </div>
);

const ExpertForm = ({ open, onClose, expertToEdit }) => {
    const { createExpert, updateExpert } = useExpertStore();

    // Identity
    const [name, setName] = useState('');
    const [imageUrl, setImageUrl] = useState('');

    // The Spectrum
    const [vibe, setVibe] = useState('casual');
    const [verbosity, setVerbosity] = useState('conversational');
    const [proactivity, setProactivity] = useState('helpful');
    const [customInstructions, setCustomInstructions] = useState('');

    const [error, setError] = useState(null);

    useEffect(() => {
        if (open && expertToEdit) {
            setName(expertToEdit.name || '');
            setImageUrl(expertToEdit.imageUrl || '');
            setVibe(expertToEdit.vibe || 'casual');
            setVerbosity(expertToEdit.verbosity || 'conversational');
            setProactivity(expertToEdit.proactivity || 'helpful');
            setCustomInstructions(expertToEdit.customInstructions || '');
            setError(null);
        } else if (open && !expertToEdit) {
            setName('');
            setImageUrl('');
            setVibe('casual');
            setVerbosity('conversational');
            setProactivity('helpful');
            setCustomInstructions('');
            setError(null);
        }
    }, [open, expertToEdit]);

    const handleSubmit = async () => {
        if (!name) {
            setError("Please name your expert.");
            return;
        }

        const expertData = {
            name,
            imageUrl,
            customInstructions,
            systemPrompt: customInstructions
        };

        try {
            if (expertToEdit) {
                await updateExpert(expertToEdit.id, expertData);
            } else {
                await createExpert(expertData);
            }

            setError(null);
            onClose();
        } catch (err) {
            setError(err.message);
        }
    };

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/20 dark:bg-black/60 backdrop-blur-sm transition-opacity duration-500" onClick={onClose} />

            <div className="relative w-full max-w-lg bg-[var(--bg-secondary)] border border-[var(--border-strong)] rounded-[2.5rem] shadow-2xl overflow-hidden flex flex-col max-h-[85vh]" onClick={e => e.stopPropagation()}>

                {/* Header */}
                <div className="px-10 py-8 flex items-center justify-between border-b border-[var(--border-subtle)]">
                    <div>
                        <h2 className="text-2xl font-semibold text-[var(--text-main)] tracking-tight">
                            {expertToEdit ? 'Agent Settings' : 'New Agent'}
                        </h2>
                        <p className="text-[13px] text-[var(--text-muted)] mt-1">
                            {expertToEdit ? 'Refine your custom AI persona.' : 'Draft a new expertise or personality for your workflows.'}
                        </p>
                    </div>
                    <button onClick={onClose} className="p-2.5 rounded-full bg-[var(--bg-tertiary)] text-[var(--text-muted)] hover:text-[var(--text-main)] transition-all">
                        <XIcon className="w-5 h-5" />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto px-10 pb-10 custom-scrollbar">
                    <div className="space-y-8 pt-8">
                        {error && (
                            <div className="p-4 rounded-2xl bg-red-500/5 border border-red-500/10 flex items-start gap-3">
                                <AlertIcon className="w-5 h-5 text-red-400 mt-0.5" />
                                <p className="text-sm text-red-300/80 font-medium">{error}</p>
                            </div>
                        )}

                        {/* Identity Section */}
                        <div className="space-y-6">
                            <div className="space-y-3">
                                <label className="block text-[10px] font-bold text-[var(--text-muted)] uppercase tracking-widest ml-1">Agent Name</label>
                                <input
                                    autoFocus
                                    type="text"
                                    placeholder="e.g. Agile Planner"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    className="w-full bg-[var(--bg-tertiary)] border border-[var(--border-strong)] text-[var(--text-main)] text-[14px] rounded-2xl px-5 py-3.5 outline-none focus:border-[var(--text-muted)]/30 transition-all placeholder-[var(--text-dim)]"
                                />
                            </div>

                            <div className="space-y-3">
                                <label className="block text-[10px] font-bold text-[var(--text-muted)] uppercase tracking-widest ml-1">Avatar URL (Optional)</label>
                                <input
                                    type="text"
                                    placeholder="https://..."
                                    value={imageUrl}
                                    onChange={(e) => setImageUrl(e.target.value)}
                                    className="w-full bg-[var(--bg-tertiary)] border border-[var(--border-strong)] text-[var(--text-main)] text-[14px] rounded-2xl px-5 py-3.5 outline-none focus:border-[var(--text-muted)]/30 transition-all placeholder-[var(--text-dim)]"
                                />
                            </div>
                        </div>

                        {/* CUSTOM INSTRUCTIONS */}
                        <div className="space-y-3">
                            <label className="block text-[10px] font-bold text-[var(--text-muted)] uppercase tracking-widest ml-1">Personality & Instructions</label>
                            <p className="text-[11px] text-[var(--text-muted)] ml-1">
                                Define the agent's expertise, rules, and behavior patterns.
                            </p>
                            <textarea
                                rows={10}
                                placeholder="e.g. You are the Agile Planner. Your job is to break projects into phases..."
                                value={customInstructions}
                                onChange={(e) => setCustomInstructions(e.target.value)}
                                className="w-full bg-[var(--bg-tertiary)] border border-[var(--border-strong)] text-[var(--text-main)] text-[14px] rounded-2xl px-5 py-4 outline-none focus:border-[var(--text-muted)]/30 resize-none transition-all leading-relaxed placeholder-[var(--text-dim)]"
                            />
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="px-10 py-8 bg-[var(--bg-main)]/30 dark:bg-[var(--bg-tertiary)]/10 border-t border-[var(--border-subtle)] flex items-center justify-end gap-3">
                    <button onClick={onClose} className="px-6 py-3 text-sm font-semibold text-[var(--text-muted)] hover:text-[var(--text-main)] transition-all uppercase tracking-widest text-[10px]">
                        Discard
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={!name.trim()}
                        className="px-8 py-3.5 bg-[var(--accent-primary)] text-[var(--bg-secondary)] hover:bg-[var(--accent-hover)] disabled:opacity-50 disabled:cursor-not-allowed text-sm font-bold rounded-[1.25rem] transition-all shadow-xl shadow-[var(--accent-primary)]/10"
                    >
                        {expertToEdit ? 'Save Changes' : 'Ignite Expert'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ExpertForm;
